import { Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { delay } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import * as data from './mock-data';

const LIST_FALLBACK: Record<string, unknown> = {
  data: [],
  reports: [],
  count: 0,
};

interface Route {
  method: string;
  pattern: RegExp;
  body: (match: RegExpMatchArray, req: HttpRequest<unknown>) => unknown;
}

const routes: Route[] = [
  { method: 'POST', pattern: /\/master\/tenants\/list$/, body: () => data.mockTenants },
  { method: 'POST', pattern: /\/master\/users\/list$/, body: () => data.mockPlatformUsers },
  { method: 'GET', pattern: /\/master\/tenants\/([^/]+)\/subscription$/, body: (m) => data.mockSubscription(m[1]) },
  { method: 'GET', pattern: /\/master\/tenants\/[^/]+\/ai-accounting$/, body: () => ({ enabled: true }) },
  { method: 'GET', pattern: /\/master\/tenants\/[^/]+\/fixed-assets$/, body: () => ({ enabled: null }) },
  { method: 'GET', pattern: /\/master\/tenants\/[^/]+\/accountants$/, body: () => data.mockAccountants },
  { method: 'GET', pattern: /\/master\/tenants\/[^/]+\/hr-staff$/, body: () => data.mockHrStaff },
  { method: 'GET', pattern: /\/master\/tenants\/([^/]+)$/, body: (m) => data.mockTenant(m[1]) },
  { method: 'GET', pattern: /\/master\/accountants(\b|\?|\/|$)/, body: () => data.mockAccountants },
  { method: 'GET', pattern: /\/master\/hr-staff(\b|\?|\/|$)/, body: () => data.mockHrStaff },
  { method: 'GET', pattern: /\/master\/platform-settings$/, body: () => data.mockPlatformSettings },
  { method: 'PUT', pattern: /\/master\/platform-settings$/, body: () => ({ message: 'ok' }) },
  { method: 'GET', pattern: /\/master\/whats-new$/, body: () => data.mockWhatsNew },
  { method: 'POST', pattern: /\/master\/whats-new$/, body: () => ({ id: 'wn-new-mock' }) },
  { method: 'PUT', pattern: /\/master\/whats-new\/[^/]+$/, body: () => ({ message: 'ok' }) },
  { method: 'DELETE', pattern: /\/master\/whats-new\/[^/]+$/, body: () => ({ message: 'ok' }) },
  { method: 'GET', pattern: /\/master\/stats$/, body: () => data.mockPlatformStats },
  { method: 'POST', pattern: /\/master\/payments\/list$/, body: () => data.mockPayments },
  { method: 'GET', pattern: /\/master\/promo-codes(\b|\?|$)/, body: () => data.mockPromoCodes },
  { method: 'POST', pattern: /\/master\/promo-codes(\b|\?|$)/, body: () => ({ id: 'promo-new-mock' }) },
  { method: 'DELETE', pattern: /\/master\/promo-codes\/[^/]+$/, body: () => ({ message: 'ok' }) },
  { method: 'GET', pattern: /\/master\/plans(\b|\?|\/|$)/, body: () => data.mockPlans },
  { method: 'GET', pattern: /\/master\/support\/tickets\/([^/]+)$/, body: (m) => data.mockTicketDetail(m[1]) },
  { method: 'GET', pattern: /\/master\/support\/tickets(\?|$)/, body: () => data.mockSupportTickets },
  { method: 'GET', pattern: /\/master\/default-invoice-template$/, body: () => data.mockDefaultTemplate },
];

@Injectable()
export class MockBackendInterceptor implements HttpInterceptor {
  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!this.isMockTarget(req.url)) {
      return next.handle(req);
    }

    if (this.isKeycloak(req.url)) {
      return this.respond(req, this.keycloakBody(req.url));
    }

    const path = this.pathOf(req.url);
    for (const route of routes) {
      if (route.method !== req.method) continue;
      const match = path.match(route.pattern);
      if (match) {
        return this.respond(req, route.body(match, req));
      }
    }

    return this.respond(req, this.fallbackBody(req));
  }

  private isMockTarget(url: string): boolean {
    return url.startsWith(environment.apiUrl) || this.isKeycloak(url);
  }

  private isKeycloak(url: string): boolean {
    return url.startsWith(environment.keycloak.url) || url.includes('/realms/');
  }

  private keycloakBody(url: string): unknown {
    if (url.includes('/logout')) return {};
    return data.mockKeycloakToken();
  }

  private pathOf(url: string): string {
    return url.startsWith(environment.apiUrl) ? url.slice(environment.apiUrl.length) : url;
  }

  private fallbackBody(req: HttpRequest<unknown>): unknown {
    if (req.responseType === 'blob') return new Blob([], { type: 'application/octet-stream' });
    if (req.method === 'DELETE' || req.method === 'PATCH' || req.method === 'PUT') return {};
    if (req.method === 'POST' && !/\/list$|\/search$/.test(this.pathOf(req.url))) {
      return { id: 'mock-' + Math.random().toString(36).slice(2, 10), ok: true };
    }
    return LIST_FALLBACK;
  }

  private respond(req: HttpRequest<unknown>, body: unknown): Observable<HttpEvent<unknown>> {
    const responseBody = req.responseType === 'blob' && !(body instanceof Blob)
      ? new Blob([JSON.stringify(body)], { type: 'application/json' })
      : body;
    return of(new HttpResponse({ status: 200, body: responseBody, url: req.url })).pipe(delay(80));
  }
}
